// frontend/src/components/schedule/EventActionModal.js
import React, { useState } from 'react';
import axios from 'axios';
import { X, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const ACTION_LABELS = {
  check_in: { label: 'Check-in', verb: 'marquer le check-in', placeholder: 'Heure d\'arrivée, état de l\'avion, remarques...', field: 'check_in', fieldValue: true },
  check_out: { label: 'Check-out', verb: 'marquer le check-out', placeholder: 'Heure de départ, état de l\'avion, remarques...', field: 'check_out', fieldValue: true },
  billing: { label: 'Facturation', verb: 'valider la facturation', placeholder: 'Montant, moyen de paiement, référence facture...', field: 'billing_status', fieldValue: 'paid' },
  grading: { label: 'Évaluation', verb: 'ajouter une évaluation', placeholder: 'Note, commentaires sur le service...', field: 'grading', fieldValue: null },
  squawk: { label: 'Squawk', verb: 'enregistrer le squawk', placeholder: 'Code squawk, problème technique...', field: 'squawk', fieldValue: null },
  fuel: { label: 'Carburant', verb: 'enregistrer le carburant', placeholder: 'Quantité (gallons), type de carburant, prix...', field: 'fuel_reimbursed', fieldValue: true }
};

const EventActionModal = ({ isOpen, onClose, eventId, eventTitle, action, onSuccess }) => {
  const { user } = useAuth();
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const actionConfig = ACTION_LABELS[action] || { 
    label: action, 
    verb: `marquer ${action}`,
    placeholder: 'Ajouter un commentaire...',
    field: action,
    fieldValue: true
  };

  const handleSubmit = async () => {
    if (!comment.trim()) {
      setError('Le commentaire est obligatoire pour cette action.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('access_token');
      
      let fieldValue = actionConfig.fieldValue;
      if (action === 'grading' || action === 'squawk') {
        fieldValue = comment.trim();
      }
      
      await axios.post(`${API_BASE}/schedule/events/${eventId}/audit`, {
        action: action,
        comment: comment.trim(),
        old_value: null,
        new_value: fieldValue !== true ? fieldValue : 'completed'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      await axios.put(`${API_BASE}/schedule/events/${eventId}`, {
        [actionConfig.field]: fieldValue
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      onSuccess();
      onClose();
      setComment('');
    } catch (err) {
      console.error('Error saving action:', err);
      setError(err.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-3 sm:p-4">
      <div className="bg-gray-800 rounded-xl w-full max-w-[95%] sm:max-w-md border border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-700">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-400" />
            <h3 className="text-white font-semibold text-sm sm:text-base">
              {actionConfig.label}
            </h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-1">
            <X className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-3 sm:p-4 space-y-3 sm:space-y-4">
          <div>
            <p className="text-gray-400 text-xs sm:text-sm">
              Vous êtes sur le point de <span className="text-white font-medium">{actionConfig.verb}</span> pour :
            </p>
            <p className="text-white font-medium mt-1 text-xs sm:text-sm bg-gray-700/50 p-2 rounded-lg">
              {eventTitle}
            </p>
          </div>

          <div>
            <label className="block text-gray-400 text-xs sm:text-sm mb-1 sm:mb-2">
              Commentaire <span className="text-red-400">*</span>
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={actionConfig.placeholder}
              rows={3}
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-xs sm:text-sm resize-none focus:outline-none focus:border-blue-500"
              autoFocus
            />
            <p className="text-gray-500 text-[10px] sm:text-xs mt-1">
              {comment.length}/500 caractères
            </p>
          </div>

          <div className="flex items-center gap-2 text-[10px] sm:text-xs text-gray-500 bg-gray-900/50 p-2 rounded-lg">
            <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-blue-500/20 flex items-center justify-center">
              <span className="text-blue-400 text-[10px] sm:text-xs font-medium">
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </span>
            </div>
            <div className="flex flex-wrap gap-1">
              <span className="text-gray-400">Effectué par :</span>
              <span className="text-white">{user?.first_name} {user?.last_name}</span>
            </div>
          </div>

          {error && (
            <div className="p-2 bg-red-500/20 border border-red-500/30 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-red-400" />
              <span className="text-red-400 text-xs sm:text-sm">{error}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 p-3 sm:p-4 border-t border-gray-700">
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 px-4 py-2 bg-green-500 hover:bg-green-600 rounded-lg text-white font-medium disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
          >
            {submitting ? (
              <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            )}
            {submitting ? 'Enregistrement...' : 'Valider'}
          </button>
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm"
          >
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
};

export default EventActionModal;